import { useEffect, useState } from 'react';
import { useDispatch } from 'react-redux';
import { useNavigate } from 'react-router';
import { Card, Column, Divider, Typography } from '@tempmee/react';
import { AccountTopper, SettingsMenuGroup } from '@tempmee/react-blocks';
import {
  OutlineChatIcon,
  OutlineLogoutIcon,
  OutlineSendAirplaneHorizontalIcon,
  OutlineShieldCheckIcon,
  OutlineUserIcon,
  SolidQuestionMarkCircleIcon,
} from '@tempmee/react-icons';
import { fetchNotificationSettings, logOut } from '~/actions';
import { AccountPageLayout } from '~/components/shared/AccountArea';
import { useNotificationTypeMap } from '~/components/NotificationSettings/hooks/useNotificationSettings';
import ConfirmPopup from '~/components/shared/ConfirmPopup';
import { Links } from '~/constants';
import { useGetMe } from '~/domain/CurrentIdentity';
import { useAppSelector } from '~/hooks/useAppSelector';
import {
  fireMenuBarHelpClicked,
  fireOfficeLogoutClicked,
  fireOfficeLogoutConfirmed,
} from '~/posthogs/events';
import { openPlatformFeedbackSurvey } from '~/posthogs/surveys';
import { routes } from '~/routes';
import { getLocationCount } from '~/utils/OfficeUtils';
import { getProfessionalName } from '~/utils/professional';
import { PersonalInformationModal } from './PersonalInformationModal';

/** One stacked contact detail (Figma `Atom/LabelStack`): an overline label over the value. */
const ContactLine = ({ label, value }: { label: string; value: string }) => (
  <Column gap={0}>
    <Typography variant="overline">{label}</Typography>
    <Typography variant="h6">{value}</Typography>
  </Column>
);

/**
 * `/my-profile` (Figma 7293-108401): the person, not a location. The name and
 * contact details come from auth-service; until it answers, the signed-in
 * office record — which carries the same person's name — stands in.
 */
export const MyProfilePage = () => {
  const dispatch = useDispatch();
  const navigate = useNavigate();
  const { data: me } = useGetMe();
  const signedInOffice = useAppSelector((state) => state.user.user);
  const locationCount = useAppSelector((state) => getLocationCount(state.user.childOffices));
  const notificationSettings = useAppSelector((state) => state.user.notificationSettings);
  const { shouldShowWarning } = useNotificationTypeMap();
  const [isPersonalInformationOpen, setIsPersonalInformationOpen] = useState(false);
  const [isLogoutOpen, setIsLogoutOpen] = useState(false);

  // Notifications edit the main account's settings, so the warning reads them too.
  useEffect(() => {
    dispatch(fetchNotificationSettings({ mainAccount: true }));
  }, [dispatch]);

  const firstName = me?.firstName ?? signedInOffice?.first_name ?? '';
  const lastName = me?.lastName ?? signedInOffice?.last_name ?? '';
  const email = me?.email ?? signedInOffice?.email_id ?? '';
  const phone = me?.phoneNumber ?? signedInOffice?.contact_number ?? '';
  const hasNotificationWarning = notificationSettings?.some(({ event, ...settings }) =>
    shouldShowWarning(event, settings),
  );

  return (
    <>
      <AccountPageLayout
        title="My profile"
        aside={
          <>
            <AccountTopper
              variant="card"
              name={getProfessionalName({ first_name: firstName, last_name: lastName }) ?? ''}
              locationCount={locationCount}
              testID="profile-topper"
            />
            <Card className="rounded-lg px-2 py-3" data-cy="profile-contact">
              <Column gap={2}>
                <ContactLine label="Email" value={email} />
                <Divider />
                <ContactLine label="Phone" value={phone} />
              </Column>
            </Card>
          </>
        }
      >
        <SettingsMenuGroup
          heading="Manage Profile"
          testID="profile-manage"
          rows={[
            {
              id: 'personal-information',
              label: 'Personal Information',
              icon: <OutlineUserIcon />,
              onSelect: () => setIsPersonalInformationOpen(true),
              testID: 'personal-information',
            },
            {
              id: 'notifications',
              label: 'Notifications',
              icon: <OutlineChatIcon />,
              alert: hasNotificationWarning ? 'error' : undefined,
              onSelect: () => navigate(routes.notificationSettings),
              testID: 'notification-settings',
            },
            {
              id: 'send-feedback',
              label: 'Send Feedback',
              icon: <OutlineSendAirplaneHorizontalIcon />,
              // Fires dental_portal:account:send_feedback_clicked itself, which opens the survey.
              onSelect: openPlatformFeedbackSurvey,
              testID: 'send-feedback',
            },
            {
              id: 'help-center',
              label: 'Help Center',
              icon: <SolidQuestionMarkCircleIcon />,
              onSelect: () => {
                fireMenuBarHelpClicked();
                navigate(routes.help);
              },
              testID: 'help-center',
            },
            {
              id: 'terms',
              label: 'Terms & Conditions',
              icon: <OutlineShieldCheckIcon />,
              href: Links.termsOfUse,
              testID: 'terms-of-service',
            },
            {
              id: 'logout',
              label: 'Logout',
              icon: <OutlineLogoutIcon />,
              onSelect: () => {
                fireOfficeLogoutClicked();
                setIsLogoutOpen(true);
              },
              testID: 'logout',
            },
          ]}
        />
      </AccountPageLayout>

      {isPersonalInformationOpen && (
        <PersonalInformationModal
          email={email}
          phone={phone}
          onClose={() => setIsPersonalInformationOpen(false)}
        />
      )}

      {isLogoutOpen && (
        <ConfirmPopup
          title="Confirm Log Out"
          description="Are you sure you would like to log out?"
          leftButtonText="Go Back"
          leftButtonAction={() => setIsLogoutOpen(false)}
          rightButtonText="Log Out"
          rightButtonAction={() => {
            fireOfficeLogoutConfirmed();
            sessionStorage.clear();
            dispatch(logOut());
            setIsLogoutOpen(false);
          }}
          dataCy="logout-confirmation-modal"
          rightButtonDataCy="btn-confirm-logout"
          leftButtonDataCy="btn-cancel-logout"
        />
      )}
    </>
  );
};
